import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type Locale = 'en' | 'zh';

interface LanguageContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
  prefix: string;
}

const translations: Record<string, { en: string; zh: string }> = {
  // Nav
  'nav.gala': { en: 'Gala', zh: '盛宴' },
  'nav.events': { en: 'Events', zh: '活动' },
  'nav.schedule': { en: 'Schedule', zh: '日程' },
  'nav.news': { en: 'News', zh: '新闻' },
  'nav.schools': { en: 'Schools', zh: '学校' },
  'nav.about': { en: 'About', zh: '关于我们' },
  'nav.contact': { en: 'Contact', zh: '联系我们' },
  'nav.home': { en: 'Home', zh: '首页' },

  // Home
  'home.eventsAndActivities': { en: 'Events & Activities', zh: '活动与项目' },
  'home.eventsSubtitle': { en: 'Discover upcoming events, workshops, and community gatherings', zh: '探索即将举行的活动、工作坊和社区聚会' },
  'home.fromAnnualGalas': { en: 'From annual galas to weekly meetings, discover what brings our community together.', zh: '从年度晚会到每周聚会，探索凝聚我们社区的各项活动。' },
  'home.allEvents': { en: 'All Events', zh: '所有活动' },
  'home.annualGala': { en: 'Annual Gala', zh: '年度晚会' },
  'home.weekly': { en: 'Weekly', zh: '每周活动' },
  'home.workshops': { en: 'Workshops', zh: '工作坊' },
  'home.community': { en: 'Community', zh: '社区' },
  'home.education': { en: 'Education', zh: '教育' },
  'home.upcomingEvents': { en: 'Upcoming Events', zh: '即将举行的活动' },
  'home.pastEvents': { en: 'Past Events', zh: '往期活动' },
  'home.pastEventsSubtitle': { en: 'Relive the highlights from our recent events', zh: '回顾近期活动的精彩瞬间' },
  'home.weeklySchedule': { en: 'Weekly Schedule', zh: '每周日程' },
  'home.regularRecurring': { en: 'Join our regular activities throughout the week', zh: '加入我们每周的常规活动' },
  'home.aboutCAAOC': { en: 'About CAAOC', zh: '关于CAAOC' },
  'home.aboutText1': {
    en: 'The Chinese-American Association of Orange County (CAAOC) has been a cornerstone of the local Chinese-American community for over 15 years. We are dedicated to preserving cultural heritage, fostering community connections, and supporting education.',
    zh: '橙县华人协会（CAAOC）十五年来一直是当地华人社区的基石。我们致力于传承文化遗产、促进社区联系和支持教育事业。',
  },
  'home.aboutText2': {
    en: 'Through our diverse programming of events, workshops, and educational initiatives, we bring together families and individuals from all backgrounds to celebrate and share Chinese-American culture.',
    zh: '通过丰富多彩的活动、工作坊和教育项目，我们将来自不同背景的家庭和个人聚集在一起，共同庆祝和分享华裔美国人的文化。',
  },
  'home.learnMore': { en: 'Learn More About Us', zh: '了解更多' },
  'home.years': { en: 'Years Serving the Community', zh: '年服务社区' },
  'home.members': { en: 'Active Members', zh: '活跃会员' },
  'home.eventsPerYear': { en: 'Events Per Year', zh: '年度活动' },
  'home.communitySubtitle': { en: 'Building bridges between cultures and generations', zh: '搭建文化与代际之间的桥梁' },
  'home.eventsBoxDesc': { en: 'Galas, festivals, picnics, and celebrations year-round', zh: '年度晚会、节日庆典、野餐和各类庆祝活动' },
  'home.educationBoxDesc': { en: 'Language schools, tutoring, and college prep programs', zh: '语言学校、辅导和大学预备课程' },
  'home.schoolsBoxDesc': { en: 'Chinese language and cultural education for all ages', zh: '面向各年龄段的中文和文化教育' },
  'home.connectingFamilies': { en: 'Connecting families and building a stronger community', zh: '连接家庭，建设更强大的社区' },
  'home.yearRoundPrograms': { en: 'Year-round programs for all ages', zh: '全年适合各年龄段的项目' },
  'home.workshopsAndSeminars': { en: 'Workshops & seminars on various topics', zh: '各类主题的工作坊和研讨会' },
  'home.irvineDistrictSupport': { en: 'Serving the Irvine and Orange County community', zh: '服务尔湾和橙县社区' },
  'home.getInTouch': { en: 'Get in Touch', zh: '联系我们' },
  'home.quickLinks': { en: 'Quick Links', zh: '快速链接' },
  'home.newsAndUpdates': { en: 'News & Updates', zh: '新闻动态' },
  'home.aboutUs': { en: 'About Us', zh: '关于我们' },
  'home.volunteerOpportunities': { en: 'Volunteer Opportunities', zh: '志愿者机会' },
  'home.viewAllEvents': { en: 'View All Events', zh: '查看所有活动' },
  'home.fullSchedule': { en: 'Full Schedule', zh: '完整日程' },
  'home.noEventsInCategory': { en: 'No upcoming events in this category. Check back soon!', zh: '该分类暂无即将举行的活动，请稍后查看！' },
  'home.schools': { en: 'Schools', zh: '学校' },
  'home.getTickets': { en: 'Get Tickets', zh: '获取门票' },
  'home.watchVideo': { en: 'Watch Video', zh: '观看视频' },

  // Contact
  'contact.getInTouch': { en: 'Get in Touch', zh: '联系我们' },
  'contact.haveQuestions': { en: 'Have a question about our events, programs, or membership? Fill out the form or reach us through any of the channels below.', zh: '对我们的活动、项目或会员有疑问？请填写表格或通过以下方式联系我们。' },
  'contact.email': { en: 'Email', zh: '电子邮箱' },
  'contact.phone': { en: 'Phone', zh: '电话' },
  'contact.location': { en: 'Location', zh: '地址' },
  'contact.orangeCounty': { en: 'Irvine, Orange County, CA', zh: '加州橙县尔湾' },
  'contact.sendMessage': { en: 'Send Message', zh: '发送消息' },
  'contact.yourName': { en: 'Your name', zh: '您的姓名' },
  'contact.yourEmail': { en: 'your@email.com', zh: '您的邮箱地址' },
  'contact.subject': { en: 'Subject', zh: '主题' },
  'contact.message': { en: 'Message', zh: '留言' },
  'contact.heroTitle': { en: 'Contact Us', zh: '联系我们' },
  'contact.heroSubtitle': { en: 'We would love to hear from you. Reach out with questions, feedback, or to learn more about CAAOC.', zh: '我们很乐意收到您的来信。如有疑问、反馈或想了解更多关于CAAOC的信息，请与我们联系。' },
  'contact.sendUsMessage': { en: 'Send Us a Message', zh: '给我们留言' },
  'contact.fullName': { en: 'Full Name', zh: '姓名' },
  'contact.phonePlaceholder': { en: '(xxx) xxx-xxxx', zh: '(xxx) xxx-xxxx' },
  'contact.selectTopic': { en: 'Select a topic', zh: '选择主题' },
  'contact.generalInquiry': { en: 'General Inquiry', zh: '一般咨询' },
  'contact.membership': { en: 'Membership', zh: '会员' },
  'contact.events': { en: 'Events', zh: '活动' },
  'contact.volunteering': { en: 'Volunteering', zh: '志愿服务' },
  'contact.sponsorship': { en: 'Sponsorship', zh: '赞助合作' },
  'contact.educationPrograms': { en: 'Education Programs', zh: '教育项目' },
  'contact.other': { en: 'Other', zh: '其他' },
  'contact.howCanWeHelp': { en: 'How can we help you?', zh: '我们能为您做什么？' },
  'contact.sending': { en: 'Sending...', zh: '发送中...' },
  'contact.messageSent': { en: 'Message Sent!', zh: '消息已发送！' },
  'contact.thankYou': { en: 'Thank you for reaching out. We will get back to you as soon as possible.', zh: '感谢您的来信，我们会尽快回复您。' },
  'contact.sendAnother': { en: 'Send Another Message', zh: '发送另一条消息' },
  'contact.respondTime': { en: 'We usually respond within 24 hours', zh: '我们通常在24小时内回复' },
  'contact.officeHours': { en: 'Office Hours', zh: '办公时间' },
  'contact.monFri': { en: 'Monday - Friday: 9:00 AM - 5:00 PM', zh: '周一至周五：上午9:00 - 下午5:00' },
  'contact.weekends': { en: 'Weekends: By appointment for events', zh: '周末：活动预约' },
  'contact.phoneTimes': { en: 'Mon-Fri, 9 AM - 5 PM PST', zh: '周一至周五，上午9点至下午5点（太平洋时间）' },
  'contact.eventsVenues': { en: 'Events held at various venues throughout OC', zh: '活动在橙县各场地举行' },
  'contact.followUs': { en: 'Follow Us', zh: '关注我们' },
  'contact.faq': { en: 'Frequently Asked Questions', zh: '常见问题' },
  'contact.faq1q': { en: 'How do I become a member of CAAOC?', zh: '如何成为CAAOC会员？' },
  'contact.faq1a': { en: 'Membership is open to everyone. You can sign up at any of our events or contact us through this form. Annual membership fees help support our programs and activities.', zh: '会员资格对所有人开放。您可以在我们的任何活动中注册，也可以通过此表格联系我们。年度会费有助于支持我们的项目和活动。' },
  'contact.faq2q': { en: 'Are events open to non-members?', zh: '非会员可以参加活动吗？' },
  'contact.faq2a': { en: 'Yes! Most of our events and activities are open to the general public. Some events may require tickets or registration. Check our Events page for details.', zh: '可以！我们的大多数活动和项目对公众开放。部分活动可能需要购票或注册。请查看我们的活动页面了解详情。' },
  'contact.faq3q': { en: 'How can I volunteer?', zh: '如何成为志愿者？' },
  'contact.faq3a': { en: 'We always welcome volunteers! Fill out the contact form with "Volunteering" as the subject, or come to any of our events and ask about volunteer opportunities.', zh: '我们随时欢迎志愿者！请填写联系表格并选择"志愿服务"主题，或者参加我们的任何活动并咨询志愿者机会。' },
  'contact.faq4q': { en: 'Do you offer Chinese language classes?', zh: '你们提供中文课程吗？' },
  'contact.faq4a': { en: 'Yes, our Chinese Language School offers Mandarin classes for children and adults at various levels. Classes are held on Saturdays. Visit our Education page for enrollment information.', zh: '是的，我们的中文学校为儿童和成人提供不同级别的普通话课程。课程在每周六举行。请访问我们的教育页面了解报名信息。' },
  'contact.faq5q': { en: 'Can I sponsor a CAAOC event?', zh: '我可以赞助CAAOC的活动吗？' },
  'contact.faq5a': { en: 'We welcome corporate and individual sponsors. Sponsorships help make our events and programs accessible to the entire community. Contact us with "Sponsorship" as the subject for more details.', zh: '我们欢迎企业和个人赞助。赞助有助于让整个社区都能参与我们的活动和项目。请以"赞助合作"为主题联系我们了解详情。' },

  // Events page
  'events.title': { en: 'Events', zh: '活动' },
  'events.subtitle': { en: 'Browse all upcoming events, workshops, and community gatherings', zh: '浏览所有即将举行的活动、工作坊和社区聚会' },
  'events.all': { en: 'All', zh: '全部' },
  'events.annualGala': { en: 'Annual Gala', zh: '年度晚会' },
  'events.weekly': { en: 'Weekly', zh: '每周活动' },
  'events.workshops': { en: 'Workshops', zh: '工作坊' },
  'events.community': { en: 'Community', zh: '社区' },
  'events.education': { en: 'Education', zh: '教育' },
  'events.grid': { en: 'Grid', zh: '网格' },
  'events.list': { en: 'List', zh: '列表' },
  'events.noEvents': { en: 'No events found in this category.', zh: '该分类暂无活动。' },
  'events.getTickets': { en: 'Get Tickets', zh: '获取门票' },
  'events.tickets': { en: 'Tickets', zh: '门票' },

  // Schedule page
  'schedule.title': { en: 'Weekly Schedule', zh: '每周日程' },
  'schedule.subtitle': { en: 'Regular activities and classes happening every week at CAAOC', zh: 'CAAOC每周定期举办的活动和课程' },
  'schedule.allDays': { en: 'All Days', zh: '所有日期' },
  'schedule.allCategories': { en: 'All Categories', zh: '所有类别' },
  'schedule.monday': { en: 'Monday', zh: '周一' },
  'schedule.tuesday': { en: 'Tuesday', zh: '周二' },
  'schedule.wednesday': { en: 'Wednesday', zh: '周三' },
  'schedule.thursday': { en: 'Thursday', zh: '周四' },
  'schedule.friday': { en: 'Friday', zh: '周五' },
  'schedule.saturday': { en: 'Saturday', zh: '周六' },
  'schedule.sunday': { en: 'Sunday', zh: '周日' },
  'schedule.today': { en: 'Today', zh: '今天' },
  'schedule.fitness': { en: 'Fitness', zh: '健身' },
  'schedule.arts': { en: 'Arts', zh: '艺术' },
  'schedule.social': { en: 'Social', zh: '社交' },
  'schedule.education': { en: 'Education', zh: '教育' },
  'schedule.music': { en: 'Music', zh: '音乐' },
  'schedule.activity': { en: 'activity', zh: '项活动' },
  'schedule.activities': { en: 'activities', zh: '项活动' },
  'schedule.note': { en: 'Schedule is subject to change. Please check our Events page for any updates or cancellations. For questions about specific activities, please', zh: '日程可能会有变动。请查看我们的活动页面获取最新信息或取消通知。如有关于具体活动的问题，请' },
  'schedule.contactUs': { en: 'contact us', zh: '联系我们' },

  // News page
  'news.title': { en: 'News', zh: '新闻' },
  'news.subtitle': { en: 'Stay updated with the latest from CAAOC and our community', zh: '了解CAAOC和社区的最新动态' },
  'news.allNews': { en: 'All News', zh: '所有新闻' },
  'news.association': { en: 'Association', zh: '协会新闻' },
  'news.community': { en: 'Community', zh: '社区新闻' },
  'news.education': { en: 'Education', zh: '教育新闻' },
  'news.featured': { en: 'Featured', zh: '精选' },
  'news.readMore': { en: 'Read More', zh: '阅读更多' },
  'news.noArticles': { en: 'No news articles in this category yet.', zh: '该分类暂无新闻文章。' },

  // About page
  'about.title': { en: 'About CAAOC', zh: '关于CAAOC' },
  'about.heroSubtitle': { en: 'The Chinese-American Association of Orange County has been connecting, celebrating, and empowering our community since 2010', zh: '橙县华人协会自2010年以来一直致力于连接、庆祝和赋能我们的社区' },
  'about.ourMission': { en: 'Our Mission', zh: '我们的使命' },
  'about.missionText1': {
    en: 'CAAOC is dedicated to serving the Chinese-American community in Orange County by preserving cultural heritage, promoting education, fostering community connections, and bridging cultural understanding. Through our diverse programs, events, and initiatives, we bring together individuals and families to celebrate our shared heritage while building a stronger, more connected community.',
    zh: 'CAAOC致力于通过传承文化遗产、推广教育、促进社区联系和增进文化理解来服务橙县华人社区。通过我们丰富多样的项目、活动和倡议，我们将个人和家庭聚集在一起，共同庆祝我们的共同遗产，同时建设一个更强大、更紧密的社区。',
  },
  'about.missionText2': {
    en: 'We believe that by honoring our past and investing in our future, we can create a vibrant, inclusive community where Chinese-American culture thrives alongside the rich diversity of Orange County.',
    zh: '我们相信，通过尊重过去、投资未来，我们可以创造一个充满活力、包容的社区，让华裔美国人的文化与橙县丰富的多元文化一起蓬勃发展。',
  },
  'about.ourValues': { en: 'Our Values', zh: '我们的价值观' },
  'about.valuesSubtitle': { en: 'The principles that guide everything we do', zh: '指导我们一切行动的原则' },
  'about.communityFirst': { en: 'Community First', zh: '社区优先' },
  'about.communityFirstDesc': { en: 'We put the needs of our community members at the center of everything we do.', zh: '我们将社区成员的需求放在我们所做一切的核心位置。' },
  'about.culturalHeritage': { en: 'Cultural Heritage', zh: '文化传承' },
  'about.culturalHeritageDesc': { en: 'We preserve and celebrate Chinese-American cultural traditions for future generations.', zh: '我们为后代保存和庆祝华裔美国人的文化传统。' },
  'about.education': { en: 'Education', zh: '教育' },
  'about.educationDesc': { en: 'We believe in the power of education to transform lives and strengthen communities.', zh: '我们相信教育能够改变生活、增强社区凝聚力。' },
  'about.inclusivity': { en: 'Inclusivity', zh: '包容性' },
  'about.inclusivityDesc': { en: 'We welcome people of all backgrounds and foster understanding between cultures.', zh: '我们欢迎所有背景的人，促进不同文化之间的理解。' },
  'about.ourJourney': { en: 'Our Journey', zh: '我们的历程' },
  'about.journeySubtitle': { en: "Milestones in CAAOC's history", zh: 'CAAOC的里程碑' },
  'about.founded': { en: 'Founded', zh: '成立' },
  'about.foundedDesc': { en: 'CAAOC was established by a group of passionate community leaders in Orange County.', zh: 'CAAOC由一群充满热情的橙县社区领袖创立。' },
  'about.firstGala': { en: 'First Annual Gala', zh: '首届年度晚会' },
  'about.firstGalaDesc': { en: 'Our first Lunar New Year Gala attracted over 200 attendees.', zh: '我们的首届农历新年晚会吸引了超过200名参加者。' },
  'about.languageSchool': { en: 'Chinese Language School', zh: '中文学校' },
  'about.languageSchoolDesc': { en: 'Launched weekend Chinese language classes for children and adults.', zh: '开办了面向儿童和成人的周末中文课程。' },
  'about.500members': { en: '500 Members', zh: '500名会员' },
  'about.500membersDesc': { en: 'Reached 500 active members and expanded programming significantly.', zh: '活跃会员达到500名，项目大幅扩展。' },
  'about.communityCenter': { en: 'Community Center', zh: '社区中心' },
  'about.communityCenterDesc': { en: 'Secured a dedicated community center space for regular activities.', zh: '获得了专门的社区中心场地用于日常活动。' },
  'about.1000members': { en: '1000+ Members', zh: '1000+会员' },
  'about.1000membersDesc': { en: 'Surpassed 1000 active members across Orange County.', zh: '橙县活跃会员超过1000名。' },
  'about.15anniversary': { en: '15th Anniversary', zh: '15周年' },
  'about.15anniversaryDesc': { en: 'Celebrated 15 years of community service with a special gala event.', zh: '以特别的晚会活动庆祝了15年的社区服务。' },
  'about.boardOfDirectors': { en: 'Board of Directors', zh: '董事会' },
  'about.boardSubtitle': { en: 'Meet the dedicated leaders guiding our organization', zh: '认识引领我们组织的杰出领导者' },
  'about.president': { en: 'President', zh: '会长' },
  'about.vicePresident': { en: 'Vice President', zh: '副会长' },
  'about.treasurer': { en: 'Treasurer', zh: '财务' },
  'about.secretary': { en: 'Secretary', zh: '秘书' },
  'about.educationDirector': { en: 'Education Director', zh: '教育总监' },
  'about.eventsDirector': { en: 'Events Director', zh: '活动总监' },
  'about.years': { en: 'Years', zh: '年' },
  'about.members': { en: 'Members', zh: '会员' },
  'about.annualEvents': { en: 'Annual Events', zh: '年度活动' },
  'about.volunteers': { en: 'Volunteers', zh: '志愿者' },
  'about.joinCommunity': { en: 'Join Our Community', zh: '加入我们的社区' },
  'about.joinText': { en: 'Whether you are new to Orange County or have been here for generations, there is a place for you at CAAOC. Come join us at an event and become part of our growing community.', zh: '无论您是新来橙县还是已经在这里生活了几代人，CAAOC都有您的一席之地。欢迎参加我们的活动，成为我们不断壮大的社区的一员。' },
  'about.browseEvents': { en: 'Browse Events', zh: '浏览活动' },
  'about.contactUs': { en: 'Contact Us', zh: '联系我们' },

  // Footer
  'footer.getInTouch': { en: 'Get in Touch', zh: '联系我们' },
  'footer.email': { en: 'Email', zh: '电子邮箱' },
  'footer.phone': { en: 'Phone', zh: '电话' },
  'footer.location': { en: 'Location', zh: '地址' },
  'footer.quickLinks': { en: 'Quick Links', zh: '快速链接' },
  'footer.home': { en: 'Home', zh: '首页' },
  'footer.events': { en: 'Events', zh: '活动' },
  'footer.schedule': { en: 'Schedule', zh: '日程' },
  'footer.news': { en: 'News', zh: '新闻' },
  'footer.schools': { en: 'Schools', zh: '学校' },
  'footer.aboutUs': { en: 'About Us', zh: '关于我们' },
  'footer.contact': { en: 'Contact', zh: '联系我们' },
  'footer.copyright': { en: 'CAAOC. All rights reserved.', zh: 'CAAOC. 版权所有。' },
  'footer.privacy': { en: 'Privacy', zh: '隐私政策' },
  'footer.terms': { en: 'Terms', zh: '使用条款' },

  // Admin
  'admin.dashboard': { en: 'Dashboard', zh: '仪表盘' },
  'admin.posts': { en: 'Posts', zh: '文章' },
  'admin.media': { en: 'Media', zh: '媒体' },
  'admin.users': { en: 'Users', zh: '用户' },
  'admin.email': { en: 'Email', zh: '邮件' },
  'admin.settings': { en: 'Settings', zh: '设置' },

  // Common
  'common.loading': { en: 'Loading...', zh: '加载中...' },
  'common.save': { en: 'Save', zh: '保存' },
  'common.cancel': { en: 'Cancel', zh: '取消' },
  'common.delete': { en: 'Delete', zh: '删除' },
  'common.edit': { en: 'Edit', zh: '编辑' },
  'common.search': { en: 'Search', zh: '搜索' },
  'common.filter': { en: 'Filter', zh: '筛选' },
  'common.note': { en: 'Note', zh: '注意' },
};

const LanguageContext = createContext<LanguageContextType | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  const getLocaleFromPath = (): Locale => {
    if (location.pathname.startsWith('/zh')) return 'zh';
    const stored = localStorage.getItem('caaoc_locale');
    if (stored === 'zh' || stored === 'en') return stored;
    return 'en';
  };

  const [locale, setLocaleState] = useState<Locale>(getLocaleFromPath);

  useEffect(() => {
    const pathLocale: Locale = location.pathname.startsWith('/zh') ? 'zh' : 'en';
    if (pathLocale !== locale) {
      setLocaleState(pathLocale);
      localStorage.setItem('caaoc_locale', pathLocale);
    }
  }, [location.pathname]);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem('caaoc_locale', newLocale);
  }, []);

  const t = useCallback(
    (key: string): string => {
      const entry = translations[key];
      if (!entry) return key;
      return entry[locale] || entry.en || key;
    },
    [locale]
  );

  const prefix = locale === 'zh' ? '/zh' : '';

  return (
    <LanguageContext.Provider value={{ locale, setLocale, t, prefix }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
